import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API_CONFIG from '../utils/apiConfig';
import './PasswordSetup.css';

const PasswordSetup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const { token, email } = location.state || {};

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
  }, [token, navigate]);

  const validateField = (name, value) => {
    switch (name) {
      case 'password':
        if (value && value.length < 8) {
          return 'Password must be at least 8 characters long';
        }
        break;
      case 'confirm_password':
        if (value && value !== formData.password) {
          return 'Passwords do not match';
        }
        break;
      default:
        return null;
    }
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Validate field
    const error = validateField(name, value);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(API_CONFIG.getApiUrl('/auth/setup-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          password: formData.password,
          confirm_password: formData.confirm_password
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Store authentication data
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('token_type', data.token_type);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Redirect to dashboard
        navigate('/dashboard');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Password setup failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="password-setup-container">
        <div className="password-setup-content">
          <h2>Access Denied</h2>
          <p>No valid token provided. Please check your email verification link.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="password-setup-container">
      <div className="password-setup-content">
        <div className="form-header">
          <h2>Set Up Your Password</h2>
          <p>Complete your account setup by creating a secure password</p>
          {email && <p className="email-info">Account: {email}</p>}
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="password-form">
          <div className="form-group">
            <label htmlFor="password">Password *</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              autoComplete="new-password"
              required
              placeholder="Enter your password (min 8 characters)"
              className={fieldErrors.password ? 'error' : ''}
            />
            {fieldErrors.password && (
              <div className="field-error">{fieldErrors.password}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirm_password">Confirm Password *</label>
            <input
              type="password"
              id="confirm_password"
              name="confirm_password"
              value={formData.confirm_password}
              onChange={handleChange}
              autoComplete="new-password"
              required
              placeholder="Confirm your password"
              className={fieldErrors.confirm_password ? 'error' : ''}
            />
            {fieldErrors.confirm_password && (
              <div className="field-error">{fieldErrors.confirm_password}</div>
            )}
          </div>

          <button 
            type="submit" 
            className="submit-btn"
            disabled={loading}
          >
            {loading ? 'Setting Up...' : 'Complete Setup'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PasswordSetup;
